import { useTranslation } from "react-i18next";
import type { PayoutMethod } from "@/types";

interface Props {
  value: PayoutMethod;
  onChange: (next: PayoutMethod) => void;
  disabled?: boolean;
}

/**
 * Two radio-style cards to choose how the therapist's subscription is billed:
 * `invoice` (registered company) or `work_contract` (ugovor o djelu, no company).
 * Drives which fields `BillingIdentityFields` renders. Rendered above it on every
 * surface that collects billing identity (settings, subscription dialog, admin).
 */
export function PayoutMethodField({ value, onChange, disabled }: Props) {
  const { t } = useTranslation();

  const options: { value: PayoutMethod; label: string; hint: string }[] = [
    { value: "invoice", label: t("billingIdentity.payoutInvoice"), hint: t("billingIdentity.payoutInvoiceHint") },
    { value: "work_contract", label: t("billingIdentity.payoutWorkContract"), hint: t("billingIdentity.payoutWorkContractHint") },
  ];

  return (
    <div className="space-y-2">
      <span className="block text-sm font-medium text-stone-700">{t("billingIdentity.payoutMethodLabel")}</span>
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            aria-pressed={value === option.value}
            className={`rounded-xl border p-4 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
              value === option.value
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border hover:border-primary/40"
            }`}
          >
            <span className="text-sm font-medium text-foreground">{option.label}</span>
            <p className="mt-1 text-xs text-muted-foreground">{option.hint}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
